<template>
  <div class="material-limits-table">
    <CustomTable
      :title="title"
      :rowsData="rowsData"
      :columns="materialLimitsColumns"
      :loading="isFetching"
      :show-search="false"
      :custom-body="true"
      :add-btn="true"
      :on-add-click="onAddClick"
    >
      <template #top-right>
        <CustomIcon
          icon="refresh"
          title="Обновить"
          :loading="isFetching"
          class="tw-mr-2"
          @click="loadData"
        />
      </template>
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.unit_name ? `${props.row.material_name} (${props.row.unit_name})` : props.row.material_name }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.unit_name }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ numberWithCommas(props.row.limit_amount) }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center" style="width: 100px; min-width: 100px;">
            <div class="tw-flex tw-justify-center tw-items-center tw-gap-2">
              <CustomIcon
                icon="edit"
                title="Редактировать"
                @click="onEditClick(props.row)"
              />
              <CustomIcon
                icon="trash"
                title="Удалить"
                :approve-mode="true"
                :approve="() => onDeleteClick(props.row)"
              />
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
    <NotFoundText v-if="!isFetching && rowsData.length === 0" />
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import NotFoundText from "@/components/common/NotFoundText";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { numberWithCommas } from "@/services";
import { MODAL_NAMES } from "@/constants";
import {
  getMaterialLimits,
  addMaterialLimit,
  updateMaterialLimit,
  deleteMaterialLimit,
  materialLimitsColumns,
} from "./services";

const props = defineProps({
  projectTypeId: {
    type: Number,
    default: null,
  },
  title: {
    type: String,
    default: "Лимиты материалов",
  },
});

const appStore = useAppStore();
const { showSucceedSnackbar } = useCustomSnackbar();

const rowsData = ref([]);
const isFetching = ref(false);

async function loadData() {
  if (!props.projectTypeId) {
    rowsData.value = [];
    return;
  }
  isFetching.value = true;
  const res = await getMaterialLimits(props.projectTypeId);
  isFetching.value = false;
  rowsData.value = res ?? [];
}

watch(
  () => props.projectTypeId,
  (id) => {
    if (id) loadData();
    else rowsData.value = [];
  },
  { immediate: true }
);

function onAddClick() {
  appStore.showModal(MODAL_NAMES.AddMaterialLimit, {
    title: "Добавить лимит материала",
    project_type_id: props.projectTypeId,
    onSubmit: async (_projectTypeId, body) => {
      const res = await addMaterialLimit(props.projectTypeId, body);
      if (!res) return;
      showSucceedSnackbar();
      appStore.closeModal();
      await loadData();
    },
  });
}

function onEditClick(row) {
  appStore.showModal(MODAL_NAMES.EditMaterialLimit, {
    title: "Редактировать лимит",
    project_type_id: props.projectTypeId,
    body: {
      project_type_material_limit_id: row.project_type_material_limit_id,
      limit_amount: row.limit_amount,
      material_name: row.material_name,
      unit_name: row.unit_name,
    },
    onSubmit: async (_projectTypeId, payload) => {
      const res = await updateMaterialLimit(props.projectTypeId, payload);
      if (!res) return;
      showSucceedSnackbar();
      appStore.closeModal();
      await loadData();
    },
  });
}

async function onDeleteClick(row) {
  const res = await deleteMaterialLimit(
    props.projectTypeId,
    row.project_type_material_limit_id
  );
  if (!res) return;
  showSucceedSnackbar();
  await loadData();
}
</script>

<style lang="scss" scoped>
.material-limits-table {
  padding: 0;
}
</style>
